import { Toast } from '../../../../components/Common/Toast';
import { callAPI } from '../../../../services/api.service';
import { HTTPStatusEnum } from '../../../../interfaces/log.interface';

export const fetchUserOrders = async (email: string, pageNumber: number) => {
  try {
    const response = await callAPI(`user/get-user-order-history?email=${email}&pageNumber=${pageNumber}`);
    return {
      orders: response?.orders || [],
      totalPages: response?.totalPages || 0
    };
  } catch (error) {
    Toast('Failed to fetch user orders', true);
    throw error;
  }
};

export const shipSelectedOrder = async (
  orderNumber: string,
  email: string | undefined,
  loader: (value: boolean) => void,
  force: boolean = false,
): Promise<{ ok?: boolean; alreadySynced?: boolean; message?: string } | void> => {
  if (!orderNumber || !email) {
    return;
  }
  try {
    loader(true);
    const response = await callAPI(`admin/orders/force/ship`, { orderNumber, email, force }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      // Bundle re-pushes return a per-child summary message (pushed/skipped/failed) — show it
      // instead of a generic line so the admin learns what actually happened.
      Toast(response?.message || 'Successfully sent to ship selected order');
      return { ok: true, message: response?.message };
    }
    if (response?.alreadySynced) {
      // Server refused because the order already reached a provider — warn (so the plain Ship button
      // gives feedback) and let the caller confirm + retry with force.
      Toast(response?.error || 'Order already sent to a provider. Use Order Recovery to force re-push.', true);
      return { alreadySynced: true };
    }
    Toast(response?.error || 'Failed to ship selected order', true);
  } catch (error) {
    Toast('Failed to ship selected order', true);
  } finally {
    loader(false);
  }
};

export interface ReleaseChild {
  orderNumber: number;
  bookId: string | null;
  bookTitle: string | null;
  quantity: number;
  fulfillmentStatus: string | null;
  printProcessingState: string | null;
  shipstationOrderId: string | null;
  ownerEmail: string;
}

// Children of a classroom release with their current statuses (read-only).
export const fetchReleaseChildren = async (email: string, orderNumber: string): Promise<ReleaseChild[]> => {
  try {
    const response = await callAPI(
      `admin/orders/release-children?email=${encodeURIComponent(email)}&orderNumber=${encodeURIComponent(orderNumber)}`,
    );
    return response?.children || [];
  } catch (error) {
    return [];
  }
};

// Returns { [bookId]: number } — how many book-print orders the user placed per book.
export const fetchBookPurchaseCounts = async (email: string): Promise<Record<string, number>> => {
  try {
    const response = await callAPI(`admin/orders/purchase-counts?email=${encodeURIComponent(email)}`);
    return response?.counts || {};
  } catch (error) {
    return {};
  }
};

// Read-only diagnostics: why an order may be stuck (route, sync state, PDF URL, legacy flag, etc.)
export const fetchOrderDiagnostics = async (email: string, orderNumber: string) => {
  try {
    const response = await callAPI(
      `admin/orders/diagnostics?email=${encodeURIComponent(email)}&orderNumber=${encodeURIComponent(orderNumber)}`,
    );
    return response?.diagnostics || null;
  } catch (error) {
    Toast('Failed to load order diagnostics', true);
    return null;
  }
};

export interface ProviderStatus {
  provider: 'shipstation' | 'lulu' | null;
  providerOrderId: string | null;
  providerStatus: string | null;
  tracking: string | null;
  carrier: string | null;
  shippedAt: string | null;
  reachable: boolean;
  fetchedAt: string | null;
}

// Read-only live provider status (ShipStation's own orderStatus) for the Shipping Provider card.
// Silent on failure — the card renders "couldn't reach provider" from a null result.
export const fetchProviderStatus = async (email: string, orderNumber: string): Promise<ProviderStatus | null> => {
  try {
    const response = await callAPI(
      `admin/orders/provider-status?email=${encodeURIComponent(email)}&orderNumber=${encodeURIComponent(orderNumber)}`,
    );
    if (!response || response.error) return null;
    return response as ProviderStatus;
  } catch {
    return null;
  }
};

// Read-only pull of the order's current state from ShipStation/Lulu, applied to the canonical
// status. Never writes to the provider. Returns the sync summary or null.
export const syncOrderFromProvider = async (
  email: string,
  orderNumber: string,
  loader: (value: boolean) => void,
): Promise<any | null> => {
  if (!orderNumber || !email) {
    return null;
  }
  try {
    loader(true);
    const response = await callAPI(`admin/orders/sync-status`, { email, orderNumber }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(
        response?.changed
          ? `Status updated: ${String(response?.before || '—').replace(/_/g, ' ')} → ${String(response?.after || '—').replace(/_/g, ' ')}`
          : 'Already up to date',
      );
      // Surface provider read failures (e.g. bad credentials) without failing the whole sync.
      (response?.outcomes || [])
        .filter((o: any) => o?.outcome === 'error')
        .forEach((o: any) => Toast(`${o.source}: ${o.detail || 'read failed'}`, true));
      return response;
    }
    Toast(response?.error || 'Failed to sync order status', true);
    return null;
  } catch (error) {
    Toast('Failed to sync order status', true);
    return null;
  } finally {
    loader(false);
  }
};

export type ManualStatusAction = 'mark_shipped' | 'cancel' | 'reset_error' | 'release_hold';

// Curated manual status correction (audited server-side). Returns true when applied.
export const applyOrderStatusAction = async (
  email: string,
  orderNumber: string,
  action: ManualStatusAction,
  opts: { note?: string; trackingNumber?: string; carrier?: string },
  loader: (value: boolean) => void,
): Promise<boolean> => {
  if (!orderNumber || !email) {
    return false;
  }
  try {
    loader(true);
    const response = await callAPI(`admin/orders/status-action`, { email, orderNumber, action, ...opts }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast(response?.message || 'Status updated');
      return true;
    }
    Toast(response?.error || response?.message || 'Status action failed', true);
    return false;
  } catch (error) {
    Toast('Status action failed', true);
    return false;
  } finally {
    loader(false);
  }
};

// Fulfillment status timeline (oldest first). Best-effort decoration — fails silently to [].
export const fetchOrderEvents = async (email: string, orderNumber: string): Promise<any[]> => {
  try {
    const response = await callAPI(
      `admin/orders/events?email=${encodeURIComponent(email)}&orderNumber=${encodeURIComponent(orderNumber)}`,
    );
    return response?.events || [];
  } catch (error) {
    return [];
  }
};

// Regenerates the print PDF and returns its URL. Does NOT email the printer or post to ShipStation.
export const regeneratePrintPdf = async (
  email: string,
  orderNumber: string,
  loader: (value: boolean) => void,
): Promise<string | null> => {
  if (!orderNumber || !email) {
    return null;
  }
  try {
    loader(true);
    const response = await callAPI(`admin/orders/regenerate-pdf`, { email, orderNumber }, 'POST');
    if (response?.status === HTTPStatusEnum.OK) {
      Toast('Print PDF regenerated');
      return response?.pdfUrlPrint || null;
    }
    Toast(response?.error || response?.message || 'Failed to regenerate PDF', true);
    return null;
  } catch (error) {
    Toast('Failed to regenerate PDF', true);
    return null;
  } finally {
    loader(false);
  }
};
